import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { get, post } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { parseTemplateEditorQuery, productLineupHref } from '@/lib/template-editor-query';
import AssignGarmentCard, { type AssignDesign, type AssignStyle } from '@/components/AssignGarmentCard';
import { GarmentThumb, LocationChips } from '@/components/LineupGarmentPicker';
import {
  findPickerCard,
  lineupPickerCards,
} from '@/lib/lineup-thumbs';
import {
  coverageLabel,
  defaultPrintPosition,
  lineupStylesMissingFromSport,
  sportCoverage,
  styleFromLineup,
  TARGET_GARMENTS,
} from '@/lib/assign-products';
import {
  isLogoOnlyArtwork,
  sortArtworkTemplates,
  sortProductTemplates,
  type ProductStyle,
} from '@/lib/store-template-kinds';

// Sport artwork-template editor — bind ≤2 designs + keep-logo for NEW stores.
// Garments and color rules come from Product lineups. This page does not
// invent print positions. Writes storeTemplates only — never live Shopify.

type Template = {
  id: string;
  name?: string;
  sport?: string;
  kind?: string;
  styles?: AssignStyle[];
};
type ProductTemplate = Template & { styles?: ProductStyle[] };
type Recipe = { id: string; name?: string; sport?: string };

const EMPTY_THUMBS: Record<string, string> = {};

export default function StoreTemplateEditor() {
  const { data: tplData, isLoading, mutate } = useSWR<{ templates?: Template[] }>('/api/store-templates', get);
  const { data: recipeData } = useSWR<{ recipes?: Recipe[] }>('/api/recipes', get);
  const { data: garmentData } = useSWR<{ garments?: Parameters<typeof lineupPickerCards>[0] }>('/api/supplier-catalog/garments', get);
  const { data: thumbData } = useSWR<{ thumbs?: Record<string, string> }>('/api/lineup-garment-thumbs', get);

  const allTemplates = tplData?.templates || [];
  const templates = sortArtworkTemplates(allTemplates);
  const productTemplates = sortProductTemplates(allTemplates as ProductTemplate[]);
  const recipes = recipeData?.recipes || [];
  const garments = garmentData?.garments || [];
  const thumbs = thumbData?.thumbs || EMPTY_THUMBS;
  const pickerCards = useMemo(() => lineupPickerCards(garments, thumbs), [garments, thumbs]);

  const [searchParams] = useSearchParams();
  const deepLink = useMemo(() => parseTemplateEditorQuery(searchParams.toString()), [searchParams]);

  const [selectedId, setSelectedId] = useState<string | null>(deepLink.templateId || null);
  const [draft, setDraft] = useState<Template | null>(null);
  const [status, setStatus] = useState('');
  const [lineupId, setLineupId] = useState('general');
  const [picking, setPicking] = useState<{ si: number; slot: number } | null>(null);
  const [highlightStyle, setHighlightStyle] = useState(deepLink.styleNumber);
  const openedDesignLink = useRef(false);

  useEffect(() => {
    if (!templates.length) return;
    if (deepLink.templateId && templates.some((t) => t.id === deepLink.templateId)) {
      setSelectedId(deepLink.templateId);
    } else if (deepLink.styleNumber) {
      const hit = templates.find((t) => (t.styles || []).some((s) => s.styleNumber.toUpperCase() === deepLink.styleNumber));
      if (hit) setSelectedId(hit.id);
    }
    if (deepLink.styleNumber) setHighlightStyle(deepLink.styleNumber);
  }, [tplData, deepLink.templateId, deepLink.styleNumber]);

  const selected = draft || templates.find((t) => t.id === selectedId) || null;
  const logoOnly = !!(selected && isLogoOnlyArtwork(selected.id));
  const coverage = sportCoverage(selected?.styles, { logoOnly });

  useEffect(() => {
    if (openedDesignLink.current || !deepLink.designId || !selected) return;
    const styles = selected.styles || [];
    const si = deepLink.styleNumber
      ? styles.findIndex((s) => s.styleNumber.toUpperCase() === deepLink.styleNumber)
      : styles.findIndex((s) => (s.designs || []).some((d) => d.designTemplateId === deepLink.designId));
    const idx = si >= 0 ? si : 0;
    if (!styles[idx]) return;
    openedDesignLink.current = true;
    setPicking({ si: idx, slot: 0 });
  }, [selected, deepLink.designId, deepLink.styleNumber]);

  const lineup = productTemplates.find((t) => t.id === lineupId) || productTemplates.find((t) => t.id === 'general') || productTemplates[0] || null;
  const addable = useMemo(
    () => lineupStylesMissingFromSport(lineup?.styles, selected?.styles),
    [lineup, selected],
  );

  const sportRecipes = useMemo(() => {
    const sport = String(selected?.sport || selected?.id || '').replace(/^sport-/, '');
    if (!sport) return recipes;
    const hits = recipes.filter((r) => (r.sport || '').replace(/_/g, '') === sport.replace(/_/g, ''));
    return hits.length ? hits : recipes;
  }, [recipes, selected]);

  function edit(mut: (t: Template) => void) {
    if (!selected) return;
    const base: Template = JSON.parse(JSON.stringify(selected));
    mut(base);
    setDraft(base);
  }

  function setDesign(si: number, slot: number, recipeId: string) {
    if (!recipeId) return;
    edit((t) => {
      const s = t.styles![si];
      const card = findPickerCard(pickerCards, s.styleNumber);
      const locations = card?.locations || [];
      const existing = (s.designs || [])[slot];
      const keepPos = existing?.position && locations.some((l) => l.position === existing.position)
        ? existing.position
        : defaultPrintPosition(locations);
      if (!keepPos) {
        setStatus(`${s.styleNumber} has no print position in the garment catalog — add one on the garment before binding designs.`);
        return;
      }
      const recipe = recipes.find((r) => r.id === recipeId);
      const next: AssignDesign = {
        designTemplateId: recipeId,
        position: keepPos,
        titleName: recipe?.name || recipeId,
        label: recipe?.name || recipeId,
      };
      const rest = (s.designs || []).filter((d, i) => i !== slot && d.designTemplateId !== recipeId);
      if (slot === 0) s.designs = [next, ...rest].slice(0, 2);
      else s.designs = [...rest.slice(0, 1), next].slice(0, 2);
    });
    setPicking(null);
  }

  function setPosition(si: number, slot: number, position: string) {
    edit((t) => {
      const s = t.styles![si];
      const card = findPickerCard(pickerCards, s.styleNumber);
      if (card && !card.locations.some((l) => l.position === position)) return;
      if (!s.designs?.[slot]) return;
      s.designs[slot].position = position;
    });
  }

  function removeDesign(si: number, slot: number) {
    edit((t) => { t.styles![si].designs!.splice(slot, 1); });
    if (picking?.si === si) setPicking(null);
  }

  function addFromLineup(style: ProductStyle) {
    if (!selected) return;
    edit((t) => {
      t.styles = t.styles || [];
      t.styles.push(styleFromLineup(style, !logoOnly) as AssignStyle);
    });
    setStatus(`Added ${style.styleNumber} from ${lineup?.name || lineup?.id || 'lineup'} — bind designs, then Save.`);
  }

  function removeStyle(si: number) {
    const s = (selected?.styles || [])[si];
    if (!s) return;
    const nDesigns = (s.designs || []).length;
    const tail = nDesigns ? ` Its ${nDesigns} attached design${nDesigns === 1 ? '' : 's'} go with it.` : '';
    if (!confirm(`Remove ${s.styleNumber} from this template?${tail} (Nothing changes until you Save.)`)) return;
    edit((t) => { t.styles!.splice(si, 1); });
    setPicking(null);
  }

  async function save() {
    if (!selected) return;
    setStatus('saving…');
    try {
      await post('/api/store-templates', selected);
      setStatus('✓ saved — new stores pick this up; live stores are unchanged');
      setDraft(null);
      mutate();
    } catch (e) {
      setStatus('save failed: ' + (e as Error).message);
    }
  }

  return (
    <div className="px-7 py-6 pb-24">
      <h1 className="text-[20px] font-semibold tracking-tighter">Assign to Products</h1>
      <p className="mb-4 text-[12px] text-muted">
        For one sport, put up to two designs on each blank and pick the logo location.
        Garments and colors come from{' '}
        <Link to={productLineupHref({ templateId: lineup?.id })} className="underline">Product lineups</Link>
        . Live stores are not rewritten.
      </p>

      <div className="grid items-start gap-5 lg:grid-cols-[240px_1fr]">
        <div className="rounded-md bg-surface p-3 shadow-card">
          {isLoading ? <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
            : templates.length ? (
              <div className="space-y-1">
                {templates.map((t) => {
                  const c = sportCoverage(t.styles, { logoOnly: isLogoOnlyArtwork(t.id) });
                  return (
                    <button
                      key={t.id}
                      onClick={() => { setSelectedId(t.id); setDraft(null); setStatus(''); setPicking(null); }}
                      className={`flex w-full flex-col rounded-sm px-2 py-1.5 text-left ${selectedId === t.id ? 'bg-primary text-white' : 'hover:bg-bg'}`}
                    >
                      <span className="truncate text-[12px]">{t.name || t.id}</span>
                      <span className={`text-[10px] ${selectedId === t.id ? 'opacity-80' : 'text-muted'}`}>{coverageLabel(c, isLogoOnlyArtwork(t.id))}</span>
                    </button>
                  );
                })}
              </div>
            ) : <div className="p-4 text-center text-[11px] text-muted">No sport templates yet.</div>}
        </div>

        <div className="rounded-md bg-surface p-4 shadow-card">
          {!selected ? <div className="p-6 text-center text-[12px] text-muted">Select a sport template.</div> : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h2 className="text-[14px] font-semibold">{selected.name || selected.id}</h2>
                {selected.sport && <Badge className="bg-success/10 text-success">{selected.sport}</Badge>}
                <span className={`text-[11px] ${coverage.garments === TARGET_GARMENTS && coverage.missingDesigns === 0 ? 'text-success' : 'text-muted'}`}>
                  {coverageLabel(coverage, logoOnly)}
                  {coverage.garments !== TARGET_GARMENTS ? ` · aim ${TARGET_GARMENTS}` : ''}
                </span>
                {coverage.overflow > 0 && (
                  <span className="text-[11px] text-red-600">{coverage.overflow} garment{coverage.overflow === 1 ? '' : 's'} have more than 2 designs.</span>
                )}
                {logoOnly && (
                  <span className="text-[11px] text-muted">Logo-only by policy — designs on general/generic are not sold.</span>
                )}
              </div>

              {!logoOnly && addable.length > 0 && (
                <div className="mb-4 rounded-md border border-dashed border-border p-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <div className="text-[11px] font-semibold text-text/80">Add from Product lineups</div>
                    {productTemplates.length > 1 && (
                      <select
                        value={lineup?.id || ''}
                        onChange={(e) => setLineupId(e.target.value)}
                        className="h-7 rounded-sm border border-border bg-white px-1.5 text-[11px]"
                      >
                        {productTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name || t.id}</option>
                        ))}
                      </select>
                    )}
                    <span className="text-[10px] text-subtle">{addable.length} not in this sport yet</span>
                  </div>
                  <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                    {addable.map((s) => {
                      const card = findPickerCard(pickerCards, s.styleNumber);
                      return (
                        <div key={s.styleNumber} className="rounded-md border border-border p-1.5">
                          <GarmentThumb src={card?.image || ''} alt={card?.name || s.styleNumber} className="aspect-square h-auto w-full" />
                          <div className="mt-1 truncate text-[11px] font-medium">{card?.name || s.styleNumber}</div>
                          <div className="truncate font-mono text-[9px] text-subtle">{s.styleNumber}</div>
                          <div className="mt-1"><LocationChips locations={card?.locations || []} empty="No location" /></div>
                          <button
                            type="button"
                            onClick={() => addFromLineup(s)}
                            className="mt-1.5 w-full rounded-sm bg-primary px-2 py-1 text-[11px] font-medium text-white"
                          >
                            Add to sport
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {(selected.styles || []).map((s, si) => (
                  <AssignGarmentCard
                    key={`${s.styleNumber}-${si}`}
                    style={s}
                    card={findPickerCard(pickerCards, s.styleNumber)}
                    recipes={recipes}
                    sportRecipes={sportRecipes}
                    sport={selected.sport}
                    logoOnly={logoOnly}
                    picking={picking?.si === si ? picking.slot : null}
                    highlighted={!!highlightStyle && s.styleNumber.toUpperCase() === highlightStyle}
                    onPickSlot={(slot) => setPicking(slot === null ? null : { si, slot })}
                    onSetDesign={(slot, id) => setDesign(si, slot, id)}
                    onSetPosition={(slot, pos) => setPosition(si, slot, pos)}
                    onRemoveDesign={(slot) => removeDesign(si, slot)}
                    onKeepLogo={(on) => edit((t) => { t.styles![si].keepLogoProduct = on; })}
                    onRemove={() => removeStyle(si)}
                  />
                ))}
                {!(selected.styles || []).length && (
                  <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted">
                    No garments on this sport yet. Add them from Product lineups above.
                  </div>
                )}
              </div>
              <div className="mt-3 text-[11px] text-muted">{status}</div>
            </>
          )}
        </div>
      </div>

      {draft && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 px-7 py-3 shadow-card backdrop-blur">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
            <div className="text-[12px] text-muted">
              Unsaved · {coverageLabel(coverage, logoOnly)}
              {coverage.missingDesigns ? ` — bind designs on ${coverage.missingDesigns} garment${coverage.missingDesigns === 1 ? '' : 's'} before a store launch.` : ''}
            </div>
            <button onClick={save} className="ml-auto rounded-sm bg-primary px-4 py-2 text-[13px] font-medium text-white">
              Save sport template
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
